var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState, useRef, useMemo } from "react";
import { Box, Stack, Typography, IconButton, FormControl, OutlinedInput, InputAdornment, Skeleton, Tooltip, CircularProgress } from "@mui/material";
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import DOMPurify from "dompurify";
import axios from "axios";
import { api_urls } from "../api/api_urls";
import AiChat from '../components/icons/AiChatIcon';
import AvaShieldIcon from '../components/icons/AvaShieldIcon';
import AvaAskHoverContentIcon from '../components/icons/AvaAskHoverContentIcon';
import { CloseIconSmall, ChatbotCloseCircleIcon } from "./icons";
import { ChatAi, ChatSlider } from "./chatbotstyles";
import { api_service } from "../api/api_service";
import i18next from "../config/translations/i18n";
import constants from "../config/constants";
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import DeleteIcon from '@mui/icons-material/Delete';
import { utility } from "../utils/index";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ListAltIcon from "@mui/icons-material/ListAlt";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { getCommaSeperatedForAmounts } from "../utils/commonUtilities";
import { app_actions } from "../actions/app_actions";
import { useLocation } from "react-router-dom";
import { NavigationConfig } from "../utils/navigation_config";
if (!DOMPurify.__chatbotRelHookInstalled) {
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
        if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
            node.setAttribute("rel", "noopener noreferrer");
        }
    });
    DOMPurify.__chatbotRelHookInstalled = true;
}
const BOT_HTML_SANITIZE_CONFIG = { ADD_ATTR: ["target"] };
export const BotMessageBubble = React.memo(function BotMessageBubble({ text, timestamp }) {
    const sanitizedHtml = useMemo(() => DOMPurify.sanitize(text, BOT_HTML_SANITIZE_CONFIG), [text]);
    return (React.createElement(Box, { className: "msgBubble" },
        React.createElement(Typography, { fontSize: "14px", fontWeight: 400, dangerouslySetInnerHTML: { __html: sanitizedHtml }, sx: { width: '100%', boxSizing: 'border-box', wordBreak: 'break-word', overflowWrap: 'break-word' } }),
        React.createElement(Box, { component: "small", className: "msgTime" }, timestamp)));
});
const Chatbot = () => {
    const [openSlider, setOpenSlider] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [voiceLoading, setVoiceLoading] = useState(false);
    const inputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const [openChatVoice, setOpenChatVoice] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [recordingTimer, setRecordingTimer] = useState(null);
    const [showPlayback, setShowPlayback] = useState(false);
    const [wasCancelled, setWasCancelled] = useState(false);
    const [t] = useTranslation('global');
    const userBasicInfo = useSelector((store) => store.User);
    const dispatch = useDispatch();
    const chatbotMessages = useSelector((store) => store.chatbot.botMessages);
    const collapsed = useSelector((store) => store.chatbot.chatbotCollapsed);
    const isLoggedIn = useSelector((store) => store.User.isLoggedIn);
    const location = useLocation();
    const [highlightTourOption, setHighlightTourOption] = useState(false);
    useEffect(() => {
        let highlightTimer = null;
        const handler = () => {
            setHighlightTourOption(true);
            setOpenSlider(true);
            if (highlightTimer)
                clearTimeout(highlightTimer);
            highlightTimer = setTimeout(() => setHighlightTourOption(false), 3600);
        };
        window.addEventListener('open-ava-chatbot', handler);
        return () => {
            window.removeEventListener('open-ava-chatbot', handler);
            if (highlightTimer)
                clearTimeout(highlightTimer);
        };
    }, []);
    useEffect(() => {
        const handler = () => setOpenSlider(true);
        window.addEventListener('open-ava-chatbot', handler);
        return () => window.removeEventListener('open-ava-chatbot', handler);
    }, []);
    useEffect(() => {
        const storedSession = localStorage.getItem("session_id");
        if (!storedSession) {
            const newSession = generateSessionId();
            localStorage.setItem("session_id", newSession);
            setSessionId(newSession);
        }
        else {
            setSessionId(storedSession);
        }
    }, []);
    useEffect(() => {
        var _a;
        (_a = messagesEndRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: "smooth" });
    }, [messages]);
    useEffect(() => {
    }, [messages]);
    useEffect(() => {
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [audioUrl]);
    useEffect(() => {
        if (isRecording) {
            if (recordingTime >= 60) {
                stopRecording();
            }
            else {
                const timer = setTimeout(() => setRecordingTime(t => t + 1), 1000);
                setRecordingTimer(timer);
            }
        }
        else {
            if (recordingTimer)
                clearTimeout(recordingTimer);
        }
    }, [isRecording, recordingTime]);
    useEffect(() => {
        const transcribeAndPrefill = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c;
            if (audioBlob && !wasCancelled) {
                setVoiceLoading(true);
                try {
                    const convertToWav = (blob) => __awaiter(void 0, void 0, void 0, function* () {
                        if (blob.type === 'audio/wav')
                            return blob;
                        const arrayBuffer = yield blob.arrayBuffer();
                        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                        const audioBuffer = yield audioCtx.decodeAudioData(arrayBuffer);
                        function encodeWAV(audioBuffer) {
                            const numChannels = audioBuffer.numberOfChannels;
                            const sampleRate = audioBuffer.sampleRate;
                            const format = 1;
                            const bitDepth = 16;
                            const samples = audioBuffer.length * numChannels;
                            const buffer = new ArrayBuffer(44 + samples * 2);
                            const view = new DataView(buffer);
                            writeString(view, 0, 'RIFF');
                            view.setUint32(4, 36 + samples * 2, true);
                            writeString(view, 8, 'WAVE');
                            writeString(view, 12, 'fmt ');
                            view.setUint32(16, 16, true);
                            view.setUint16(20, format, true);
                            view.setUint16(22, numChannels, true);
                            view.setUint32(24, sampleRate, true);
                            view.setUint32(28, sampleRate * numChannels * 2, true);
                            view.setUint16(32, numChannels * 2, true);
                            view.setUint16(34, bitDepth, true);
                            writeString(view, 36, 'data');
                            view.setUint32(40, samples * 2, true);
                            let offset = 44;
                            for (let i = 0; i < audioBuffer.length; i++) {
                                for (let channel = 0; channel < numChannels; channel++) {
                                    let sample = audioBuffer.getChannelData(channel)[i];
                                    sample = Math.max(-1, Math.min(1, sample));
                                    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
                                    offset += 2;
                                }
                            }
                            return new Blob([buffer], { type: 'audio/wav' });
                        }
                        function writeString(view, offset, str) {
                            for (let i = 0; i < str.length; i++) {
                                view.setUint8(offset + i, str.charCodeAt(i));
                            }
                        }
                        return encodeWAV(audioBuffer);
                    });
                    const wavBlob = yield convertToWav(audioBlob);
                    const formData = new FormData();
                    formData.append('LanguageType', 'en');
                    formData.append('File', wavBlob, 'audio.wav');
                    const res = yield api_service.post({
                        endPoint: api_urls.CHATBOT_TRANSCRIBE,
                        payLoad: formData,
                        headerType: 'multipart/form-data',
                    });
                    setVoiceLoading(false);
                    if (((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.statusCode) === 200 && ((_c = (_b = res === null || res === void 0 ? void 0 : res.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.transcript)) {
                        if (res.data.data.transcript.trim() === "No transcript found.") {
                            setInput("");
                        }
                        else {
                            const formatted = formatBotResponse(res.data.data.transcript);
                            setInput(prevInput => prevInput ? `${prevInput} ${formatted}` : formatted);
                        }
                    }
                    else {
                    }
                }
                catch (error) {
                    setVoiceLoading(false);
                }
                setAudioBlob(null);
                setShowPlayback(false);
                setOpenChatVoice(false);
                setWasCancelled(false);
            }
            else if (wasCancelled) {
                setAudioBlob(null);
                setShowPlayback(false);
                setOpenChatVoice(false);
                setWasCancelled(false);
            }
        });
        if (audioBlob && !isRecording) {
            transcribeAndPrefill();
        }
    }, [audioBlob, isRecording]);
    const generateSessionId = () => Math.random().toString(36).substring(2, 12);
    const logout = () => {
        const newSession = generateSessionId();
        localStorage.setItem("session_id", newSession);
        setSessionId(newSession);
        setMessages([]);
        setOpenSlider(false);
    };
    const wasLoggedInRef = useRef(isLoggedIn);
    useEffect(() => {
        if (wasLoggedInRef.current && !isLoggedIn) {
            logout();
        }
        wasLoggedInRef.current = isLoggedIn;
    }, [isLoggedIn]);
    const sendMessageToChatbot = (message, sessionId) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g;
        setLoading(true);
        try {
            const res = yield api_service.post({
                endPoint: api_urls.CHATBOT_API_URL,
                payLoad: {
                    Payload: {
                        Query: message,
                        Session_id: sessionId,
                        Domain: ((_a = process.env) === null || _a === void 0 ? void 0 : _a.LOCAL_DEV_ENV) === constants.ENV.LOCAL_DEV ? 'https://qa-vendor1.servicehqtrs.com' : (_b = window === null || window === void 0 ? void 0 : window.location) === null || _b === void 0 ? void 0 : _b.origin,
                        DealerBuildVersion: (_c = process.env) === null || _c === void 0 ? void 0 : _c.APP_VERSION,
                        Language: i18next.language,
                        BaseUrl: (_d = process.env) === null || _d === void 0 ? void 0 : _d.API_DOMAIN,
                    }
                },
                showLoader: false
            });
            setLoading(false);
            if (((_e = res === null || res === void 0 ? void 0 : res.data) === null || _e === void 0 ? void 0 : _e.statusCode) === 200) {
                return (_g = (_f = res === null || res === void 0 ? void 0 : res.data) === null || _f === void 0 ? void 0 : _f.data) === null || _g === void 0 ? void 0 : _g.data;
            }
            else {
                return null;
            }
        }
        catch (error) {
            setLoading(false);
            return null;
        }
    });
    const startRecording = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setWasCancelled(false);
            const stream = yield navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            setMediaRecorder(recorder);
            setRecordingTime(0);
            setIsRecording(true);
            setShowPlayback(false);
            setAudioBlob(null);
            setAudioUrl(null);
            let localChunks = [];
            recorder.ondataavailable = (e) => {
                localChunks.push(e.data);
            };
            recorder.onstop = () => {
                const blob = new Blob(localChunks, { type: 'audio/webm' });
                setAudioBlob(blob);
                setAudioUrl(URL.createObjectURL(blob));
                setIsRecording(false);
                setShowPlayback(true);
                stream.getTracks().forEach(track => track.stop());
            };
            recorder.start();
        }
        catch (err) {
            utility.alert(t('Chatbot.MicrophoneAccessDenied'), constants.TOAST_TYPES.ERROR);
            setIsRecording(false);
            setOpenChatVoice(false);
        }
    });
    const stopRecording = () => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
        }
        setIsRecording(false);
        setOpenChatVoice(false);
        setRecordingTime(0);
        if (recordingTimer)
            clearTimeout(recordingTimer);
    };
    const cancelRecording = () => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
        }
        setIsRecording(false);
        setOpenChatVoice(false);
        setRecordingTime(0);
        setAudioBlob(null);
        setAudioUrl(null);
        setShowPlayback(false);
        setWasCancelled(true);
        if (recordingTimer)
            clearTimeout(recordingTimer);
    };
    const voiceHandiler = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!isRecording && !showPlayback) {
            setOpenChatVoice(true);
            yield startRecording();
        }
        else if (isRecording) {
            stopRecording();
        }
        else if (showPlayback) {
            setShowPlayback(false);
            setAudioBlob(null);
            setAudioUrl(null);
            setOpenChatVoice(false);
        }
    });
    const handleSendAudio = () => __awaiter(void 0, void 0, void 0, function* () {
        var _h;
        if (audioBlob) {
            setLoading(true);
            try {
                const blobToBase64 = (blob) => {
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            var _a;
                            const base64data = (_a = reader.result) === null || _a === void 0 ? void 0 : _a.toString().split(',')[1];
                            if (base64data)
                                resolve(base64data);
                            else
                                reject('Failed to convert blob to base64');
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                };
                const base64Audio = yield blobToBase64(audioBlob);
                const res = yield axios.post('/transcribe', { audio: base64Audio });
                setLoading(false);
                if ((_h = res === null || res === void 0 ? void 0 : res.data) === null || _h === void 0 ? void 0 : _h.text) {
                    const formatted = formatBotResponse(res.data.text);
                    setInput(res.data.text);
                }
                else {
                    setInput('');
                }
            }
            catch (error) {
                setLoading(false);
                setInput('');
            }
            setAudioBlob(null);
            setShowPlayback(false);
            setOpenChatVoice(false);
        }
    });
    const getCustomErrorMessage = (str) => {
        const lowerStr = str.toLowerCase();
        if (lowerStr.includes("error: keyerror")) {
            return "No results found for the provided Transaction Id or Contract Id.";
        }
        if (lowerStr.includes("contract id (aspireappid)")) {
            return str.replace(/Contract Id \(AspireAppId\)/gi, "Contract Id");
        }
        return null;
    };
    const buildRow = (key, finalTerms, arr, IsLoan) => {
        let __rowHeader = key;
        if (key === "MonthlyPayment") {
            __rowHeader = "Monthly Payment";
        }
        else if (key === "PurchaseOption") {
            __rowHeader = "Purchase Option";
        }
        else if (key === "Rate") {
            __rowHeader = "Rate";
        }
        let rowHtml = `<tr><td style='border:1px solid #ccc;padding:4px 8px;font-weight:bold;background:#f1f2f4;white-space:nowrap;'>${__rowHeader}</td>`;
        finalTerms.forEach(term => {
            let value = "";
            for (const item of arr) {
                value = getDirectValue(item, key, term, IsLoan);
                if (value !== "")
                    break;
            }
            rowHtml += `<td style='border:1px solid #ccc;padding:4px 8px;white-space:nowrap;'>${value !== "" ? `${key === 'Rate' ? `${Number(value).toFixed(2)}%` : `${getCommaSeperatedForAmounts(value)}`}` : "-"}</td>`;
        });
        rowHtml += "</tr>";
        return rowHtml;
    };
    const getDirectValue = (row, key, term, IsLoan) => {
        const originalKey = key;
        if (key === 'Rate') {
            if (IsLoan) {
                key = 'MonthlyPayment';
            }
            else {
                key = 'PurchaseOption';
            }
        }
        let val = row[key];
        if (Array.isArray(val)) {
            const found = val.find((v) => String(v.Term) === term);
            if (found) {
                if (originalKey !== 'Rate' && found.Amount !== null && found.Amount !== undefined)
                    return found.Amount;
                if (originalKey === 'Rate' && found.Rate !== null && found.Rate !== undefined)
                    return found.Rate;
            }
        }
        return "";
    };
    const buildPaymentOptionTable = (arr, isLoan) => {
        const termsSet = new Set();
        arr.forEach((row) => {
            ["MonthlyPayment", "PurchaseOption"].forEach((key) => {
                const val = row[key];
                if (Array.isArray(val)) {
                    val.forEach((item) => {
                        if (item.Amount !== undefined && item.Amount !== null && item.Amount !== "") {
                            termsSet.add(String(item.Term));
                        }
                    });
                }
                else if (val && typeof val === "object" && val.Amount !== undefined && val.Amount !== null && val.Amount !== "") {
                    termsSet.add(String(val.Term));
                }
            });
        });
        if (termsSet.size === 0) {
            arr.forEach((row) => {
                if (row.Amount !== undefined && row.Amount !== null && row.Amount !== "") {
                    termsSet.add(String(row.Term));
                }
            });
        }
        const terms = Array.from(termsSet).sort((a, b) => Number(a) - Number(b));
        const finalTerms = terms.length > 0 ? terms : ["12", "24", "36", "48"];
        let table = "<table style='border-collapse:collapse;width:100%;margin:8px 0;font-size:14px;'>";
        table += "<thead><tr><th style='border:1px solid #ccc;padding:4px 8px;background:#f1f2f4;text-align: left;white-space:nowrap;'>Terms</th>";
        finalTerms.forEach(term => {
            table += `<th style='border:1px solid #ccc;padding:4px 8px;background:#f7f7f7;white-space:nowrap;'>${term}</th>`;
        });
        table += "</tr></thead><tbody>";
        table += buildRow("MonthlyPayment", finalTerms, arr, isLoan);
        if (isLoan) {
            table += buildRow("Rate", finalTerms, arr, isLoan);
        }
        else {
            table += buildRow("PurchaseOption", finalTerms, arr, isLoan);
        }
        table += "</tbody>";
        const disclaimer = `<tr><td colspan='${finalTerms.length + 1}' style='padding:8px;font-size:13px;color:#000000;font-style:italic;background:#fffbe7;border:1px solid #ffe082;white-space:normal;'>Disclaimer: This quotation is an estimate only. Anything provided by the chatbot should not be considered final.</td></tr>`;
        table += `<tfoot>${disclaimer}</tfoot>`;
        table += "</table>";
        return `<div style='overflow-x:auto;-webkit-overflow-scrolling:touch;'>${table}</div>`;
    };
    const buildGenericTable = (data) => {
        if (!data || (typeof data !== "object"))
            return "";
        const arr = Array.isArray(data) ? data : [data];
        if (arr.length === 0)
            return "<i>No data available</i>";
        const headers = Array.from(arr.reduce((set, row) => {
            Object.keys(row).forEach((k) => set.add(k));
            return set;
        }, new Set())).filter((h) => h !== "LeaseType" && h !== "RateFactor" && h !== "MonthlyPayment" && h !== "PurchaseOption");
        if (headers.length === 0)
            return "";
        let table = "<table style='border-collapse:collapse;width:100%;margin:8px 0;font-size:14px'>";
        table += "<thead><tr>" + headers.map((h) => `<th style='border:1px solid #ccc;padding: 12px 8px;background:#f7f7f7;white-space:nowrap;'>${h}</th>`).join("") + "</tr></thead>";
        table += "<tbody>";
        arr.forEach((row) => {
            table += "<tr>" + headers.map((h) => {
                const cell = row[h];
                if (cell && typeof cell === "object") {
                    return `<td style='border:1px solid #ccc;padding:4px 8px; font-size:12px'>${buildGenericTable(cell)}</td>`;
                }
                else {
                    return `<td style='border:1px solid #ccc;padding:4px 8px; font-size:12px;white-space:nowrap;'>${cell !== undefined ? cell : ""}</td>`;
                }
            }).join("") + "</tr>";
        });
        table += "</tbody></table>";
        return `<div style='overflow-x:auto;-webkit-overflow-scrolling:touch;'>${table}</div>`;
    };
    const parseCircularJson = (input) => {
        if (typeof input === "string") {
            try {
                const trimmed = input.trim();
                if ((trimmed.startsWith("{") && trimmed.endsWith("}")) ||
                    (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
                    const parsed = JSON.parse(trimmed);
                    return parseCircularJson(parsed);
                }
            }
            catch (_a) {
                return input;
            }
        }
        if (Array.isArray(input)) {
            return input.map(parseCircularJson);
        }
        if (input && typeof input === "object") {
            const result = {};
            for (const key in input) {
                result[key] = parseCircularJson(input[key]);
            }
            return result;
        }
        return input;
    };
    const formatBotResponse = (response) => {
        let processedResponse = response;
        const customError = getCustomErrorMessage(processedResponse);
        if (customError) {
            return customError;
        }
        if (processedResponse.includes("|#|@|")) {
            processedResponse = processedResponse.replace("|#|@|", `<button class="show-main-menu-btn" style="width: 100%; margin-top: 8px; padding:8px 16px;background:#1976d2;color:#fff;border:none;border-radius:4px;cursor:pointer;">Show Main Menu</button>`);
        }
        let jsonData = null;
        let isLoan = false;
        try {
            const trimmed = processedResponse.trim();
            if ((trimmed.startsWith("{") && trimmed.endsWith("}")) ||
                (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
                const parsed = JSON.parse(trimmed);
                isLoan = (parsed === null || parsed === void 0 ? void 0 : parsed.IsLoan) === 1;
                const deepParsed = parseCircularJson(parsed && typeof parsed === "object" && "data" in parsed ? parsed.data : parsed);
                jsonData = deepParsed;
            }
        }
        catch (e) {
            jsonData = null;
        }
        if (jsonData && (Array.isArray(jsonData) || typeof jsonData === "object")) {
            const arr = Array.isArray(jsonData) ? jsonData : [jsonData];
            const hasPaymentOption = arr.some(row => "MonthlyPayment" in row || "PurchaseOption" in row);
            let html = "";
            if (hasPaymentOption) {
                html += buildPaymentOptionTable(arr, isLoan);
            }
            else {
                html += buildGenericTable(arr);
            }
            return html || "<i>No data available</i>";
        }
        processedResponse = processedResponse.replace(/\n(\d+)\.?\s*(.*)/g, '\n$1. $2');
        const lines = processedResponse.split(/\n/);
        const isNumberedList = lines.length > 1 &&
            lines[0].trim().startsWith("1.") &&
            lines.every((line, idx) => line.trim().startsWith(`${idx + 1}.`));
        if (isNumberedList) {
            const listItems = lines
                .map((line) => {
                const content = line.replace(/^\d+\.\s*/, "");
                return `<li>${content.trim()}</li>`;
            })
                .filter((line) => line !== "<li></li>")
                .join("");
            return `<ol>${listItems}</ol>`;
        }
        let number = 1;
        return lines
            .map((line) => {
            if (/^###\s(.*)/.test(line)) {
                line = line.replace(/^###\s(.*)/g, "<h3>$1</h3>");
            }
            else if (/^##\s(.*)/.test(line)) {
                line = line.replace(/^##\s(.*)/g, "<h2>$1</h2>");
            }
            else if (/^#\s(.*)/.test(line)) {
                line = line.replace(/^#\s(.*)/g, "<h1>$1</h1>");
            }
            line = line.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
            line = line.replace(/\*(.*?)\*/g, "<i>$1</i>");
            line = line.replace(/_(.*?)_/g, "<i>$1</i>");
            line = line.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank">$1</a>');
            line = line.replace(/^[-*•]\s(.*)/g, "• $1");
            return line.trim();
        })
            .filter((line) => line !== "")
            .join("<br />");
    };
    const getCurrentTime = () => {
        const now = new Date();
        const hours = now.getHours() % 12 || 12;
        const minutes = now.getMinutes().toString().padStart(2, "0");
        const ampm = now.getHours() >= 12 ? "PM" : "AM";
        return `${hours}:${minutes} ${ampm}`;
    };
    const handleSendMessage = () => __awaiter(void 0, void 0, void 0, function* () {
        var _j;
        if (audioBlob) {
            yield handleSendAudio();
            return;
        }
        if (!input.trim())
            return;
        const timestamp = getCurrentTime();
        setMessages((prev) => [...prev, { text: input, type: "user", timestamp }]);
        setInput("");
        setMessages((prev) => [...prev, { text: "", type: "bot", timestamp: "" }]);
        const botResponse = yield sendMessageToChatbot(input, sessionId);
        if (botResponse) {
            setMessages((prev) => [
                ...prev.slice(0, -1),
                { text: formatBotResponse(botResponse), type: "bot", timestamp: getCurrentTime() }
            ]);
        }
        else {
            setMessages((prev) => [
                ...prev.slice(0, -1),
                {
                    text: "⚠️ Sorry, something went wrong. Please try again later.",
                    type: "bot",
                    timestamp: getCurrentTime(),
                },
            ]);
        }
        setInput("");
        (_j = inputRef.current) === null || _j === void 0 ? void 0 : _j.focus();
    });
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !loading && !voiceLoading) {
            if (e.shiftKey) {
                e.preventDefault();
                setInput((prev) => prev + "\n");
            }
            else {
                e.preventDefault();
                handleSendMessage();
            }
        }
    };
    const toggleDrawer = (newOpen) => () => {
        setOpenSlider(newOpen);
        if (newOpen)
            setHighlightTourOption(false);
        if (!newOpen && isRecording) {
            setWasCancelled(true);
            stopRecording();
        }
    };
    const isOnDashboard = location.pathname === NavigationConfig.Dashboard;
    const isOnApplicationsList = location.pathname === NavigationConfig.Applications;
    const currentScreenTourLabel = isOnDashboard
        ? 'Quick Tour of Sales Dashboard'
        : isOnApplicationsList
            ? 'Quick Tour of Applications List'
            : null;
    const menuOptions = [
        {
            id: 1,
            label: 'Application Status',
            apiQuestion: 'what is the application status?',
            note: 'Track your application progress and updates.',
            icon: React.createElement(AssignmentTurnedInIcon, null),
            isTour: false,
        },
        {
            id: 2,
            label: 'Loan and Lease Programs',
            apiQuestion: 'what are the available loan & lease programs?',
            note: 'Display the list of programs',
            icon: React.createElement(ListAltIcon, null),
            isTour: false,
        },
        {
            id: 3,
            label: 'Quotation',
            apiQuestion: 'can you generate a quote for the available Equipment and Rates?',
            note: 'Generate a Quote for the available Equipment and Rates',
            icon: React.createElement(RequestQuoteIcon, null),
            isTour: false,
        },
        ...(currentScreenTourLabel ? [{
                id: 4,
                label: currentScreenTourLabel,
                apiQuestion: '',
                note: 'AVA will walk you through the current screen',
                icon: React.createElement(AssignmentTurnedInIcon, null),
                isTour: true,
            }] : []),
    ];
    const handleMenuOptionClick = (option) => __awaiter(void 0, void 0, void 0, function* () {
        if (option.isTour) {
            setOpenSlider(false);
            window.dispatchEvent(new CustomEvent('start-product-tour'));
            return;
        }
        if (!sessionId)
            return;
        setMessages(prev => [
            ...prev,
            { text: option.label, type: "user", timestamp: getCurrentTime() },
            { text: "", type: "bot", timestamp: "" }
        ]);
        setLoading(true);
        const botResponse = yield sendMessageToChatbot(option.apiQuestion, sessionId);
        setLoading(false);
        if (botResponse) {
            setMessages(prev => [
                ...prev.slice(0, -1),
                { text: formatBotResponse(botResponse), type: "bot", timestamp: getCurrentTime() }
            ]);
        }
        else {
            setMessages(prev => [
                ...prev.slice(0, -1),
                {
                    text: "⚠️ Sorry, something went wrong. Please try again later.",
                    type: "bot",
                    timestamp: getCurrentTime(),
                },
            ]);
        }
    });
    const DrawerList = () => {
        var _a;
        return (React.createElement(Box, { className: 'cstSlider' },
            React.createElement(Stack, { className: 'sliderHeader', direction: 'row', alignItems: 'center', justifyContent: 'space-between', p: 2 },
                React.createElement(Box, { display: 'flex', alignItems: 'center', columnGap: 1 },
                    React.createElement(Box, { className: 'chatHeaderIcon' },
                        React.createElement(AiChat, { inverse: true })),
                    React.createElement(Typography, { className: 'chatHeaderTitle', fontSize: '16px', fontWeight: 500 }, "Ask AVA")),
                React.createElement(IconButton, { onClick: toggleDrawer(false) },
                    React.createElement(CloseIconSmall, null))),
            React.createElement(Stack, { p: 2, pb: 1, className: 'sliderBody', justifyContent: 'space-between' },
                React.createElement(Stack, { rowGap: 1, className: "sliderContent" },
                    React.createElement(Box, { className: 'welcomeMessage' },
                        React.createElement(Typography, { className: 'welcomeTitle' },
                            "Welcome ", (_a = userBasicInfo === null || userBasicInfo === void 0 ? void 0 : userBasicInfo.userData) === null || _a === void 0 ? void 0 :
                            _a.firstName,
                            "!"),
                        React.createElement(Typography, { className: 'welcomeDesc hasLine' }, "For quick answers, ask AVA about the topics below or search using your own question."),
                        React.createElement(Typography, { className: 'welcomeDesc' }, "You can choose an option below or ask me anything.")),
                    menuOptions.map((option, idx) => (React.createElement(Box, { key: idx, className: 'menuOptionWrap' },
                        React.createElement(Stack, { sx: { cursor: 'pointer' }, className: `menu-options ${option.isTour ? 'menu-options-tour' : ''} ${option.isTour && highlightTourOption ? 'menu-options-tour-animate' : ''}`, direction: 'column', alignItems: 'flex-start', gap: 0.25, width: '100%', onClick: () => handleMenuOptionClick(option) },
                            React.createElement(Typography, { className: 'menuTitle', fontWeight: 600 }, option.label),
                            React.createElement(Typography, { className: 'menuNote', fontSize: 12 }, option.note))))),
                    React.createElement(Box, { className: 'menuTimestamp' }, getCurrentTime()),
                    messages.map((msg, index) => (msg.type === "bot" ? (React.createElement(Box, { key: index, className: "reciever", display: "flex", alignItems: "flex-end", columnGap: 1.5 },
                        React.createElement(Box, { className: "botAvatar" },
                            React.createElement(AiChat, null)),
                        React.createElement(Box, { className: "recieverText" }, msg.text ? (React.createElement(BotMessageBubble, { text: msg.text, timestamp: msg.timestamp })) : (React.createElement(Skeleton, { variant: "rounded", width: 200, height: 40, animation: "wave" }))))) : (React.createElement(Box, { key: index, className: "sender", display: "flex", justifyContent: "flex-end" },
                        React.createElement(Box, { className: "senderText" },
                            React.createElement(Box, { className: "msgBubble" },
                                React.createElement(Typography, { fontSize: "14px", fontWeight: 400 }, msg.text),
                                React.createElement(Box, { component: "small", className: "msgTime" }, msg.timestamp))))))),
                    React.createElement("div", { ref: messagesEndRef }))),
            React.createElement(Stack, { pb: 2, pt: 1, className: 'sliderFooter' },
                React.createElement(FormControl, { variant: "outlined", fullWidth: true },
                    React.createElement(OutlinedInput, { id: "outlined-chat-input", placeholder: 'Type here or use voice to chat...', multiline: true, startAdornment: (isRecording) ? (React.createElement(InputAdornment, { position: "start", sx: { pr: 1 } }, isRecording ? (React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', minWidth: 70 } },
                            [0, 1, 2, 3, 4].map(i => (React.createElement(Box, { key: i, sx: {
                                    width: 5,
                                    height: 18,
                                    mx: 0.4,
                                    borderRadius: 1,
                                    background: '#1976d2',
                                    animation: `recordBarAnim 1s ${i * 0.1}s infinite ease-in-out`,
                                    '@keyframes recordBarAnim': {
                                        '0%, 100%': { height: 10 },
                                        '50%': { height: 28 },
                                    },
                                } }))),
                            React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { ml: 1 } },
                                recordingTime,
                                "s"),
                            React.createElement(IconButton, { size: "small", onClick: cancelRecording, sx: { ml: 1, p: 0.5 } },
                                React.createElement(DeleteIcon, { fontSize: "small" })))) : null)) : null, endAdornment: React.createElement(InputAdornment, { position: "end" },
                            !isRecording && !showPlayback && !voiceLoading && (React.createElement(Tooltip, { title: 'Voice Assistant', placement: "top-start" },
                                React.createElement(IconButton, { className: 'chatActionBtn', onClick: voiceHandiler, edge: "end", sx: { mr: 0.5 } },
                                    React.createElement(KeyboardVoiceIcon, null)))),
                            isRecording && !voiceLoading && (React.createElement(IconButton, { onClick: voiceHandiler, edge: "end", className: "animated-icon-button" },
                                React.createElement(Box, { className: "ripple-wrapper" },
                                    React.createElement(Box, { className: "ripple-circle" })),
                                React.createElement(GraphicEqIcon, null))),
                            voiceLoading && (React.createElement(CircularProgress, { size: 20, color: "primary", sx: { mr: 1 } })),
                            React.createElement(IconButton, { className: 'chatActionBtn sendBtn', onClick: handleSendMessage, disabled: (!input.trim() && !audioBlob) || loading || isRecording || voiceLoading, edge: "end" },
                                React.createElement(SendOutlinedIcon, null))), type: "text", name: "input", onChange: (e) => setInput(e.target.value), value: input, autoComplete: "off", inputRef: inputRef, onKeyDown: (e) => handleKeyDown(e), disabled: isRecording })))));
    };
    useEffect(() => {
        const handler = () => {
            setMessages(prev => [
                ...prev,
                {
                    text: menuOptions.map((option, idx) => `<div class="menuOptionWrap">
                            <div class="menu-option-rendered menu-options ${option.isTour ? 'menu-options-tour' : ''} ${option.isTour && highlightTourOption ? 'menu-options-tour-animate' : ''}" data-idx="${idx}" style="display:flex;flex-direction:column;align-items:flex-start;gap:2px;cursor:pointer;">
                                <div class="menuTitle">${option.label}</div>
                                <div class="menuNote">${option.note}</div>
                            </div>
                        </div>`).join(""),
                    type: "bot",
                    timestamp: getCurrentTime()
                }
            ]);
        };
        const menuButtons = document.querySelectorAll(".show-main-menu-btn");
        menuButtons.forEach((el) => {
            el.onclick = handler;
        });
        setTimeout(() => {
            const renderedOptions = document.querySelectorAll(".menu-option-rendered");
            renderedOptions.forEach((el) => {
                el.addEventListener("click", () => {
                    const idx = Number(el.getAttribute("data-idx"));
                    const option = menuOptions[idx];
                    if (option) {
                        handleMenuOptionClick(option);
                    }
                });
            });
        }, 0);
        return () => {
            document.querySelectorAll(".show-main-menu-btn").forEach((el) => {
                el.onclick = null;
            });
            const renderedOptions = document.querySelectorAll(".menu-option-rendered");
            renderedOptions.forEach((el) => {
                el.replaceWith(el.cloneNode(true));
            });
        };
    }, [messages]);
    useEffect(() => {
        const reduxMessages = (chatbotMessages !== null && chatbotMessages !== void 0 ? chatbotMessages : []);
        if (reduxMessages && reduxMessages.length > 0) {
            setMessages(reduxMessages);
        }
    }, []);
    useEffect(() => {
        dispatch(app_actions.setBotMessages(messages));
    }, [messages]);
    const handleCollapse = () => {
        dispatch(app_actions.setChatbotCollapsed(true));
        setOpenSlider(false);
    };
    const handleExpand = () => {
        dispatch(app_actions.setChatbotCollapsed(false));
        setOpenSlider(true);
        setHighlightTourOption(false);
    };
    return (React.createElement(ChatAi, null,
        React.createElement(Box, null,
            !openSlider && (collapsed ? (React.createElement(IconButton, { className: 'cstChatBtn cstChatBtn--collapsed', onClick: handleExpand, sx: (theme) => ({
                    right: '-20px !important',
                    bottom: `calc(${theme.spacing(12.5)} + 0px) !important`,
                    borderRadius: '14px 0 0 14px !important',
                    width: '44px !important',
                    minWidth: 'unset !important',
                }) },
                React.createElement(Box, { className: "cstChatIdleContent" },
                    React.createElement(AvaShieldIcon, null)))) : (React.createElement(Box, { className: "cstChatHoverZone" },
                React.createElement(IconButton, { className: 'cstChatBtn cstChatBtn--in-hover-zone', onClick: toggleDrawer(true) },
                    React.createElement(Box, { className: "cstChatIdleContent" },
                        React.createElement(AvaShieldIcon, null)),
                    React.createElement(Box, { className: "cstChatHoverContent" },
                        React.createElement(AvaAskHoverContentIcon, null)))))),
            !collapsed && !openSlider && (React.createElement(Box, { component: 'span', onClick: handleCollapse, sx: (theme) => ({
                    position: 'fixed',
                    right: `calc(${theme.spacing(4)} + 14px)`,
                    bottom: `calc(${theme.spacing(12.5)} + 20px)`,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    zIndex: 1299,
                }) },
                React.createElement(ChatbotCloseCircleIcon, null)))),
        React.createElement(ChatSlider, { open: openSlider, onClose: toggleDrawer(false), anchor: 'right' }, DrawerList())));
};
export default Chatbot;
